import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { StreamConnectionError, StreamConnectionState } from '@/infra'
import {
  createAuthenticationError,
  createHttpError,
  createInitialStreamConnectionState,
  createNetworkError,
  isRetriableError,
} from '@/infra'
import type { ConnectionConfirmation, StreamConfiguration } from '@/index'
import { parseConnectionConfirmation, parseSSEChunk, useStreamReconnection } from '@/index'
import { env } from '@/infra/config/env'

export interface UseStreamConnectionOptions {
  readonly config: StreamConfiguration
  readonly accessToken: string | undefined
  readonly enabled: boolean
  readonly onEvent?: ((data: string) => void) | undefined
  readonly onConnected?: ((connectionId: string) => void) | undefined
  readonly onDisconnected?: (() => void) | undefined
  readonly onError?: ((error: StreamConnectionError) => void) | undefined
}

export interface UseStreamConnectionReturn {
  readonly state: StreamConnectionState
  readonly connect: () => Promise<void>
  readonly disconnect: () => Promise<void>
  readonly retry: () => void
  readonly isConnected: boolean
  readonly isConnecting: boolean
  readonly hasError: boolean
}

export function useStreamConnection(
  options: UseStreamConnectionOptions,
): UseStreamConnectionReturn {
  const { config, accessToken, enabled, onEvent, onConnected, onDisconnected, onError } = options

  const [state, setState] = useState<StreamConnectionState>(createInitialStreamConnectionState)

  const abortControllerRef = useRef<AbortController | null>(null)
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null)
  const connectionIdRef = useRef<string | null>(null)

  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent

  const reconnection = useStreamReconnection()

  const closeConnection = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    if (readerRef.current) {
      readerRef.current.cancel().catch(() => {})
      readerRef.current = null
    }
  }, [])

  const notifyBackendDisconnect = useCallback(async () => {
    const connectionId = connectionIdRef.current
    if (!connectionId || !accessToken) return

    try {
      const url = `${env.BACKEND_BASE_URL}${config.endpoints.disconnect}/${connectionId}`
      await fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
    } catch {
      // Best effort - don't block disconnect on backend failure
    }
  }, [accessToken, config.endpoints.disconnect])

  const handleConnectionConfirmation = useCallback(
    (confirmation: ConnectionConfirmation) => {
      connectionIdRef.current = confirmation.connectionId
      setState((prev) => ({
        ...prev,
        status: 'connected',
        connectionId: confirmation.connectionId,
        error: null,
      }))
      reconnection.resetAttempts()
      onConnected?.(confirmation.connectionId)
    },
    [onConnected, reconnection],
  )

  const processMessages = useCallback(
    (buffer: string): string => {
      const messages = buffer.split('\n\n')
      const remainingBuffer = messages.pop() ?? ''

      for (const message of messages) {
        if (!message.trim()) continue

        const sseChunk = parseSSEChunk(message)
        if (!sseChunk?.data) continue

        if (sseChunk.event === 'connected') {
          const result = parseConnectionConfirmation(sseChunk.data, config.type)
          if (result.success) {
            handleConnectionConfirmation(result.payload)
          }
        } else if (config.eventConfig.eventNames.includes(sseChunk.event ?? '')) {
          onEventRef.current?.(sseChunk.data)
        }
      }

      return remainingBuffer
    },
    [config.type, config.eventConfig.eventNames, handleConnectionConfirmation],
  )

  const readStream = useCallback(
    async (reader: ReadableStreamDefaultReader<Uint8Array>, signal: AbortSignal) => {
      const decoder = new TextDecoder()
      let buffer = ''

      try {
        while (!signal.aborted) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          buffer = processMessages(buffer)
        }
      } catch (error) {
        if (!signal.aborted) {
          throw error
        }
      }
    },
    [processMessages],
  )

  const handleError = useCallback(
    (error: StreamConnectionError) => {
      closeConnection()
      onError?.(error)

      if (isRetriableError(error) && !reconnection.isMaxAttemptsReached()) {
        reconnection.incrementAttempts()
        setState((prev) => ({
          ...prev,
          status: 'error',
          error,
        }))
        reconnection.scheduleReconnection(() => {})
      } else {
        setState((prev) => ({
          ...prev,
          status: 'error',
          connectionId: null,
          error,
        }))
      }
    },
    [closeConnection, onError, reconnection],
  )

  const connect = useCallback(async () => {
    if (!accessToken) {
      handleError(createAuthenticationError('No access token provided', config.type))
      return
    }

    if (abortControllerRef.current || readerRef.current) {
      return
    }

    reconnection.cancelReconnection()
    closeConnection()

    setState((prev) => ({
      ...prev,
      status: 'connecting',
      error: null,
    }))

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      const url = `${env.BACKEND_BASE_URL}${config.endpoints.connect}`
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
        signal: abortController.signal,
      })

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleError(
            createAuthenticationError(`Authentication failed: ${response.statusText}`, config.type),
          )
        } else {
          handleError(createHttpError(response.status, response.statusText, config.type))
        }
        return
      }

      if (!response.body) {
        handleError(createNetworkError('No response body', config.type))
        return
      }

      const reader = response.body.getReader()
      readerRef.current = reader

      await readStream(reader, abortController.signal)

      if (!abortController.signal.aborted) {
        setState((prev) => ({
          ...prev,
          status: 'disconnected',
          connectionId: null,
        }))
        onDisconnected?.()
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return
      }

      const message = error instanceof Error ? error.message : 'Connection failed'
      handleError(createNetworkError(message, config.type))
    }
  }, [accessToken, config, closeConnection, handleError, readStream, reconnection, onDisconnected])

  const disconnect = useCallback(async () => {
    reconnection.cancelReconnection()
    await notifyBackendDisconnect()
    closeConnection()
    connectionIdRef.current = null
    setState(createInitialStreamConnectionState())
    onDisconnected?.()
  }, [notifyBackendDisconnect, closeConnection, reconnection, onDisconnected])

  const retry = useCallback(() => {
    reconnection.resetAttempts()
    void disconnect()
    if (enabled && accessToken) {
      void connect()
    }
  }, [reconnection, disconnect, enabled, accessToken, connect])

  useEffect(() => {
    if (enabled && accessToken) {
      if (
        abortControllerRef.current ||
        readerRef.current ||
        state.status === 'connected' ||
        state.status === 'connecting'
      ) {
        return
      }
      void connect()
    } else if (!enabled && (state.status === 'connected' || state.status === 'connecting')) {
      // Intentional disconnect when disabled (e.g., logout)
      reconnection.cancelReconnection()
      void notifyBackendDisconnect()
      closeConnection()
      connectionIdRef.current = null
      setState(createInitialStreamConnectionState())
      onDisconnected?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, accessToken])

  useEffect(() => {
    return () => {
      reconnection.cancelReconnection()
      // Fire and forget - can't await in cleanup
      void notifyBackendDisconnect()
      closeConnection()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isConnected = useMemo(() => state.status === 'connected', [state.status])
  const isConnecting = useMemo(() => state.status === 'connecting', [state.status])
  const hasError = useMemo(() => state.status === 'error', [state.status])

  return {
    state,
    connect,
    disconnect,
    retry,
    isConnected,
    isConnecting,
    hasError,
  }
}
